import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';

/*
 * Props.
 */

interface BasicViewerBaseProps {
  isPreviousActivityBasic: boolean;
  date?: number;
  color?: string;
}

/*
 * Style.
 */

// This grid container is rendered outside in places such as conversationTimeline.tsx or basicViewerResnooze.tsx
export const StyledBasicGroupDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  align-items: center;
  justify-items: center;
`;

const StyledTimestampWrapperDiv = styled.div`
  /** Full-width to allow displaying on hover for entire cell. */
  width: 100%;
  vertical-align: middle;

  text-align: right;
  justify-self: end;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};

  /* Visible on hover. */
  opacity: 0;
  &:hover {
    opacity: 1;
  }
`;
const StyledTimestampDiv = styled.div`
  position: relative;
  margin-top: 12px;
  margin-right: 4px;
`;

interface ContentStyleProps {
  $isPreviousActivityBasic?: boolean;
  $color?: string;
}
const StyledContentDiv = styled.div<ContentStyleProps>`
  margin-top: ${(p) => (p.$isPreviousActivityBasic ? '12px' : '16px')};

  grid-template-columns: auto 1fr;
  grid-auto-flow: column;
  justify-self: start;
  position: relative;

  font-size: ${fontSizes.verySmall};
  font-style: normal;
  color: ${(p) => p.$color || p.theme.greys.shade80};

  ${ellipsis()};

  &:hover + div {
    opacity: 1;
  }

  overflow: visible;
`;

/*
 * Component.
 */

export const BasicViewerBase: FC<PropsWithChildren<BasicViewerBaseProps>> = (props) => {
  const {color = '', isPreviousActivityBasic} = props;

  // Return content with a grid spacer div on the left-hand side, and
  // timestamp (or another grid spacer) on the right-hand side.
  return (
    <>
      <div />
      <StyledContentDiv $isPreviousActivityBasic={isPreviousActivityBasic} $color={color}>
        {props.children}
      </StyledContentDiv>
      {(props.date && (
        <StyledTimestampWrapperDiv>
          <StyledTimestampDiv>
            <InteractiveDateTimeFormatter
              relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
              value={props.date}
            />
          </StyledTimestampDiv>
        </StyledTimestampWrapperDiv>
      )) || <div />}
    </>
  );
};
